import React from 'react';
import moment from 'moment';

import {
  SearchTextField,
  ToggleSwitch,
  MCButton,
  PopoverMenu,
  AsyncSelectField,
  MCText
} from 'src/components/basic';
import { TableLibrary } from 'src/components/complex';
import BaseComponent from 'src/components/BaseComponent';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import AlertManager from 'src/components/alert/AlertManager';

import { remCss, lang, permissions, scssVariables, rgba, withRouter } from 'src/helpers';

import model from './model';
import ShortInfo from 'src/components/user/ShortInfo';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { Cell, Row } from '@table-library/react-table-library/table';
import LANG_CONST from 'src/helpers/lang/const';

class AdminManagers extends BaseComponent {
  componentDidMount() {
    model.subscribe(this.subscription);
    if (!model.initFlag) {
      model.refresh();
      model.initFlag = true;
    }
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
  }

  renderAction = (item, isClinic = false) => {
    if (!item) return null;
    return (
      <div className={'mc-flex'} style={{ justifyContent: 'flex-end' }}>
        <MCButton
          label={'Login'}
          small
          radius
          primary
          dark
          onClick={() => {
            permissions.switchToFront();
            model.logAsManager(item);
          }}
        />
        <PopoverMenu
          placement={'bottom-end'}
          menu={() => (
            <ul
              className={'mc-select-field-items'}
              style={{
                backgroundColor: 'white',
                padding: remCss(10)
              }}
            >
              <li>
                <MCButton
                  type={'button'}
                  iconname={BASE_ICONS.CLOSE}
                  width={48}
                  padding={7}
                  light
                  radius
                  secondary
                  onClick={() =>
                    AlertManager.popup(AlertManager.TYPES.WARN, {
                      close: false,
                      title: LANG_CONST.notice.delete,
                      cancelButton: {
                        label: LANG_CONST.buttons.no,
                        action: (actions) => actions.hide()
                      },
                      actionButton: {
                        label: LANG_CONST.buttons.yes,
                        action: (actions) => {
                          actions.setLoading(true);
                          const fn = isClinic ? 'removeClinic' : 'remove';
                          model[fn](item, () => {
                            actions.setLoading(false);
                            actions.hide();
                          });
                        }
                      }
                    })
                  }
                />
              </li>
            </ul>
          )}
          className={'mc-no-style-btn'}
        >
          {() => <SVGIcon iconname={BASE_ICONS.DOTS} width={30} />}
        </PopoverMenu>
      </div>
    );
  };

  render() {
    let clinicId = -1;
    let loading = model.refreshing();
    let noResults = model.noResults();
    let searchQuery = model.listInput('search');
    let items = model.items();
    let { total = 0 } = model.getCurrent();
    const createState = model.getApiState('switchFlags');

    const columns = [
      {
        label: LANG_CONST.name,
        sort: { sortKey: 'name' },
        resize: true,
        renderCell: (item) => {
          return <ShortInfo user={item} />;
        }
      },
      {
        label: LANG_CONST.email_phone,
        resize: true,
        minWidth: '250',
        width: '25%',
        renderCell: (item) => (
          <div className={'mc-flex-5-col'}>
            <MCText label={item.email} numberOfLines={1} heading size={18} weight={500} />
            <MCText
              label={formatPhoneNumberIntl((item.phoneNumber || '').toString())}
              numberOfLines={1}
              heading
              size={18}
              weight={400}
            />
          </div>
        )
      },
      {
        label: LANG_CONST.added_date,
        resize: true,
        // sort: { sortKey: 'managerAddedDate' },
        minWidth: '180',
        width: '15%',
        renderCell: (item) =>
          item.managerAddedDate ? moment(item.managerAddedDate).format('MMMM D, YYYY') : ''
      },
      {
        label: LANG_CONST.active,
        resize: true,
        minWidth: '120',
        width: '12%',
        renderCell: (item) => {
          return (
            <ToggleSwitch
              disabled={createState.loading}
              checked={item.managerActive}
              onChange={() => model.active(item)}
            />
          );
        }
      },
      {
        label: LANG_CONST.message,
        resize: true,
        minWidth: '120',
        width: '12%',
        renderCell: (item) => {
          return (
            <div className={'mc-flex'}>
              <MCButton
                label={LANG_CONST.message}
                primary
                dark
                small
                radius
                type={'link'}
                to={permissions.PAGES.ADMIN_MESSAGES_USER_PAGE.match(item.id)}
              />
            </div>
          );
        }
      },
      {
        label: LANG_CONST.actions,
        cellStyles: { textAlign: 'end' },
        width: `${remCss(150)}px`,
        renderCell: (item) => this.renderAction(item, false)
      }
    ].filter((v) => !!v);

    return (
      <div className={'mc-container-content mc-all-patients'}>
        <div className={'mc-container-content__header mc-flex-15'}>
          <span className={'mc-grow'}>{lang.t(LANG_CONST.titles.managers)}</span>
          <SearchTextField
            width={300}
            template={'2'}
            onChange={(text) => {
              model.setStateSilent({
                pageIndex: 0
              });
              model.setListStateSilent({ items: [], total: 0 }, 'search');
              model.handleChangeSearch(text);
              if (!text) {
                model.setListState({ items: [] }, model.getCurrentId(), null, true);
                model.refresh();
              }
            }}
            value={model.listInput('search')}
          />
          <div style={{ width: remCss(300), minWidth: remCss(200) }}>
            <AsyncSelectField
              template={6}
              isClearable={true}
              apiConfig={{
                url: 'admin/clinic/list',
                labelFn: (item) => <ShortInfo user={item} small />
              }}
              placeholder={LANG_CONST.clinic}
              name={'clinicId'}
              value={model.input('clinicId')}
              onChange={(n, v) => {
                if (v?.toString() === model.input('clinicId')?.toString()) return;
                model.setStateSilent({
                  pageIndex: 0,
                  clinicId: v
                });
                model.setListStateSilent({ items: [], total: 0 }, model.getCurrentId());
                model.refresh();
              }}
            />
          </div>
        </div>
        <div className={'mc-container-content__body'}>
          <div
            className={'mc-container-content__body-content'}
            style={{ marginTop: remCss(25), zIndex: 0 }}
          >
            <TableLibrary
              loading={loading}
              noResults={noResults}
              searchQuery={searchQuery}
              sortBy={model.state.sortBy}
              selectedPage={model.state.pageIndex}
              total={total}
              limit={model.listLimit}
              renderPrefixExtend={(item) => {
                if (clinicId === item.clinic?.id) return null;
                clinicId = item.clinic?.id;

                if (!clinicId)
                  return (
                    <React.Fragment key={clinicId}>
                      <Row
                        item={item.clinic}
                        style={{
                          background: rgba(scssVariables['colors-mc-primary'], 0.3),
                          borderRadius: remCss(5)
                        }}
                      >
                        <Cell key={clinicId + '_info'} gridColumnStart={1} gridColumnEnd={7}>
                          <div className={'mc-text-518'}>{lang.t(LANG_CONST.no_clinic)}</div>
                        </Cell>
                      </Row>
                    </React.Fragment>
                  );

                return (
                  <React.Fragment key={clinicId}>
                    <Row
                      item={item.clinic}
                      style={{
                        background: rgba(scssVariables['colors-mc-primary'], 0.15),
                        borderRadius: remCss(5)
                      }}
                    >
                      <Cell key={clinicId + '_info'} gridColumnStart={1} gridColumnEnd={5}>
                        <ShortInfo user={item.clinic} />
                      </Cell>
                      <Cell key={clinicId + '_msg'} gridColumnStart={5} gridColumnEnd={6}>
                        <div className={'mc-flex'}>
                          <MCButton
                            label={LANG_CONST.message}
                            primary
                            dark
                            small
                            radius
                            type={'link'}
                            to={permissions.PAGES.ADMIN_MESSAGES_USER_PAGE.match(clinicId)}
                          />
                        </div>
                      </Cell>
                      <Cell key={clinicId + '_action'}>{this.renderAction(item.clinic, true)}</Cell>
                    </Row>
                  </React.Fragment>
                );
              }}
              onSortChange={(sortBy) => {
                model.handleChange('sortBy', sortBy);
                model.setListState({ items: [] }, model.getCurrentId(), null, true);
                model.loadMore();
              }}
              onSetPage={(v) => {
                model.handleChange('pageIndex', v);
                model.setListState({ items: [] }, model.getCurrentId(), null, true);
                model.loadMore();
              }}
              columns={columns}
              data={items}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(AdminManagers);
